import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { loanApplicationSignalRConnection } from 'app/services/signalR';

import { PresenceSignalRMessageType } from 'constants/presence';
import useLoanId from 'hooks/useLoanId';
import { logger } from 'utils/logger';

import { getUuid } from 'store/auth/selectors';
import { selectAllFieldLocks } from 'store/presence/selectors';

import { RELEASE_TIMEOUT_MS } from './constants';
import { globalReleaseGeneration, withTimeout } from './utils';

const useReleaseMyFieldLocks = () => {
  const loanId = useLoanId();
  const fieldLocks = useSelector(selectAllFieldLocks);
  const currentUserId = useSelector(getUuid);

  const releaseMyFieldLocks = useCallback(() => {
    globalReleaseGeneration.current += 1;
    const generation = globalReleaseGeneration.current;

    if (!loanId) {
      return Promise.resolve();
    }

    const myLocks = Object.entries(fieldLocks).filter(([, lock]) => lock.userId === currentUserId);

    if (!myLocks.length) {
      return Promise.resolve();
    }

    const releasePromises = myLocks.map(([fieldPath]) =>
      withTimeout(
        loanApplicationSignalRConnection.invoke(
          PresenceSignalRMessageType.RELEASE_FIELD_LOCK,
          loanId,
          fieldPath,
          null,
        ),
        RELEASE_TIMEOUT_MS,
      ).catch(err => {
        if (globalReleaseGeneration.current !== generation) {
          return;
        }

        logger.error('Failed to release field lock on modal close', {
          loanId,
          fieldPath,
          err,
        });
      }),
    );

    return Promise.allSettled(releasePromises);
  }, [fieldLocks, currentUserId, loanId]);

  return releaseMyFieldLocks;
};

export default useReleaseMyFieldLocks;
